import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { C, SERIF, SANS, mono } from '../../theme';
import { Icon } from '../../icons';

/* ------------------------------------------------------------------ */
/*  Shared Clay form primitives for the owner create/manage sheets.    */
/*  All four forms (AddTenancy / EditTenancy / RecordPayment /         */
/*  InviteTenant) are built from these so the chrome stays consistent  */
/*  and mobile-friendly (full-viewport overlay, scrollable body).      */
/* ------------------------------------------------------------------ */

/** A modal sheet that overlays the whole viewport (escapes the app card). */
export function Sheet({
  icon = 'key',
  title,
  subtitle,
  onClose,
  children,
  footer,
  busy,
}: {
  icon?: string;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  busy?: boolean;
}) {
  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, busy]);

  return (
    <div
      onClick={() => !busy && onClose()}
      /* Defers a service-worker auto-update reload while a sheet is open, so an
         update never discards half-entered rent or tenant details. See pwa.ts. */
      data-pwa-busy=""
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 120,
        background: 'rgba(30,22,16,.44)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 0,
        fontFamily: SANS,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: C.bg,
          width: '100%',
          maxWidth: 540,
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '18px 18px 0 0',
          boxShadow: '0 -8px 40px rgba(20,18,14,.28)',
          overflow: 'hidden',
        }}
      >
        {/* header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '18px 20px 14px',
            borderBottom: `1px solid ${C.line}`,
            background: C.card,
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: C.soft,
              color: C.accent2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <Icon n={icon} size={19} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 19.5, margin: 0, fontWeight: 600, color: C.ink }}>{title}</h2>
            {subtitle && <div style={{ color: C.muted, fontSize: 12.8, marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button
            onClick={() => !busy && onClose()}
            aria-label="Close"
            style={{
              border: 0,
              background: 'transparent',
              color: C.muted,
              cursor: busy ? 'default' : 'pointer',
              fontSize: 22,
              lineHeight: 1,
              padding: '2px 4px',
              flex: 'none',
            }}
          >
            ×
          </button>
        </div>

        {/* scrollable body */}
        <div style={{ padding: '16px 20px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {children}
        </div>

        {/* sticky footer */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '12px 20px',
            borderTop: `1px solid ${C.line}`,
            background: C.card,
          }}
        >
          {footer}
        </div>
      </div>
    </div>
  );
}

/** Inline error banner — shows the API message on failure (never silent). */
export function ErrorBanner({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      style={{
        background: C.redSoft,
        border: `1px solid #e6c6ba`,
        color: C.red,
        borderRadius: 10,
        padding: '10px 12px',
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {message}
    </div>
  );
}

/** A labelled field wrapper. */
export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12.5, color: C.ink2, fontWeight: 600, marginBottom: 5 }}>
        {label}
        {required && <span style={{ color: C.accent, marginLeft: 3 }}>*</span>}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11.6, color: C.muted, marginTop: 5, lineHeight: 1.35 }}>{hint}</div>}
    </label>
  );
}

/** A responsive 2-up grid for side-by-side fields (stacks on very narrow screens). */
export function FieldRow({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
      {children}
    </div>
  );
}

const baseInput: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  fontSize: 14,
  fontFamily: 'inherit',
  color: C.ink,
  background: C.card,
  outline: 'none',
};

function focusOn(e: { currentTarget: HTMLElement }) {
  e.currentTarget.style.borderColor = C.accent;
  e.currentTarget.style.boxShadow = `0 0 0 3px ${C.soft}`;
}
function focusOff(e: { currentTarget: HTMLElement }) {
  e.currentTarget.style.borderColor = C.line;
  e.currentTarget.style.boxShadow = 'none';
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal';
}) {
  return (
    <input
      type={type}
      value={value}
      autoFocus={autoFocus}
      inputMode={inputMode}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={focusOn}
      onBlur={focusOff}
      style={baseInput}
    />
  );
}

/** ₹-prefixed money input. Emits the raw rupee string; convert with rupeesToPaise. */
export function MoneyInput({
  value,
  onChange,
  placeholder = '0',
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'stretch', border: `1px solid ${C.line}`, borderRadius: 9, overflow: 'hidden', background: C.card }}
      onFocus={focusOn}
      onBlur={focusOff}
      tabIndex={-1}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          background: C.line2,
          color: C.ink2,
          fontWeight: 600,
          fontSize: 14,
          ...mono(),
        }}
      >
        ₹
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
        style={{ ...baseInput, border: 0, borderRadius: 0, ...mono() }}
      />
    </div>
  );
}

export function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={focusOn}
      onBlur={focusOff}
      style={{ ...baseInput, appearance: 'none', cursor: 'pointer' }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** A pill segmented control (2–3 options). */
export function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div style={{ display: 'flex', gap: 4, background: C.line2, borderRadius: 10, padding: 3 }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              padding: '8px 6px',
              borderRadius: 8,
              border: 0,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              background: on ? C.card : 'transparent',
              color: on ? C.accent : C.muted,
              boxShadow: on ? '0 1px 2px rgba(20,18,14,.12)' : 'none',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A labelled toggle row (used for the advanced mode switches). */
export function ToggleRow({
  label,
  hint,
  on,
  onToggle,
  children,
}: {
  label: string;
  hint?: string;
  on: boolean;
  onToggle: (v: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: '11px 13px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.3, fontWeight: 600, color: C.ink }}>{label}</div>
          {hint && <div style={{ fontSize: 11.6, color: C.muted, marginTop: 2, lineHeight: 1.35 }}>{hint}</div>}
        </div>
        <button
          type="button"
          onClick={() => onToggle(!on)}
          aria-label={label}
          aria-pressed={on}
          style={{
            width: 40,
            height: 23,
            borderRadius: 999,
            background: on ? C.accent : '#cdd3cc',
            position: 'relative',
            border: 0,
            cursor: 'pointer',
            flex: 'none',
            transition: 'background .15s',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: on ? 19 : 2,
              width: 19,
              height: 19,
              borderRadius: 999,
              background: '#fff',
              transition: 'left .15s',
            }}
          />
        </button>
      </div>
      {on && children && <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>}
    </div>
  );
}

/** A collapsible "Advanced" section. */
export function Collapsible({
  open,
  onToggle,
  label,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          color: C.accent,
          fontWeight: 600,
          fontSize: 13,
          padding: '4px 0',
          fontFamily: 'inherit',
        }}
      >
        <Icon n="chev" size={15} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        {label}
      </button>
      {open && <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>}
    </div>
  );
}

/** Section heading inside the sheet body. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10.8,
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        color: C.muted,
        fontWeight: 700,
        marginTop: 2,
      }}
    >
      {children}
    </div>
  );
}

export function PrimaryBtn({
  children,
  onClick,
  disabled,
  busy,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        flex: 1,
        background: C.accent,
        color: '#fff',
        border: 0,
        borderRadius: 10,
        padding: '12px 16px',
        fontWeight: 650,
        fontSize: 14,
        fontFamily: 'inherit',
        cursor: disabled || busy ? 'default' : 'pointer',
        opacity: disabled || busy ? 0.6 : 1,
      }}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

export function GhostBtn({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 'none',
        background: C.card,
        color: C.ink2,
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        padding: '12px 18px',
        fontWeight: 600,
        fontSize: 14,
        fontFamily: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Conversion helpers (money = integer paise, dates = epoch ms UTC).  */
/* ------------------------------------------------------------------ */

/** Rupee string → integer paise. Returns null for empty/invalid. */
export function rupeesToPaise(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** Integer paise → rupee input string (whole rupees; drops trailing .00). */
export function paiseToRupees(paise: number | null | undefined): string {
  if (paise == null) return '';
  const r = paise / 100;
  return Number.isInteger(r) ? String(r) : r.toFixed(2);
}

/** "YYYY-MM-DD" → epoch ms at UTC midnight. Returns null for empty/invalid. */
export function dateInputToEpoch(s: string): number | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d);
}

/** epoch ms → "YYYY-MM-DD" (UTC) for a date input value. */
export function epochToDateInput(ms: number | null | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** Percent string → basis points (5 → 500). Returns null for empty/invalid. */
export function pctToBps(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** Current "YYYY-MM" (UTC). */
export function currentPeriodUTC(now = Date.now()): string {
  const d = new Date(now);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
